import React from 'react';
import { motion } from 'framer-motion';
import { FileText, ExternalLink, Eye } from 'lucide-react';
import Card, { CardContent, CardHeader } from '../ui/Card';

import { DocumentSource } from '../../services/streamingService';

interface SourceDisplayProps {
  sources: DocumentSource[];
  confidence?: number;
  processingTime?: number;
  tokenCount?: number;
  onCitationClick?: (documentId: string, documentName: string, chunks: DocumentSource[]) => void;
  className?: string;
}

const SourceDisplay: React.FC<SourceDisplayProps> = ({
  sources,
  confidence,
  processingTime,
  tokenCount,
  onCitationClick,
  className = '',
}) => {
  if (!sources || sources.length === 0) {
    return null;
  }

  const handleViewDocument = (source: DocumentSource) => {
    if (onCitationClick) {
      // Group chunks by document ID
      const documentChunks = sources.filter(s => s.documentId === source.documentId);
      onCitationClick(
        source.documentId.toString(),
        `Document ${source.documentId}`, // We could fetch the actual name from API if needed
        documentChunks
      );
    }
  };

  const formatProcessingTime = (ms?: number) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatConfidence = (conf?: number) => {
    if (!conf) return 'N/A';
    return `${(conf * 100).toFixed(1)}%`;
  };

  const getSimilarityColor = (similarity: number) => {
    if (similarity >= 0.8) return 'text-green-600 bg-green-100';
    if (similarity >= 0.6) return 'text-yellow-600 bg-yellow-100';
    return 'text-orange-600 bg-orange-100';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`mt-4 ${className}`}
    >
      <Card className="border border-gray-200 bg-gray-50">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-medium text-gray-700">
                Sources ({sources.length})
              </span>
            </div>
            {confidence && (
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span>Confidence: <strong>{formatConfidence(confidence)}</strong></span>
                {processingTime && (
                  <span>Time: <strong>{formatProcessingTime(processingTime)}</strong></span>
                )}
                {tokenCount && (
                  <span>Tokens: <strong>{tokenCount}</strong></span>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            {sources.map((source, index) => (
              <motion.div
                key={source.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: index * 0.1 }}
                className="p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-gray-500">
                        Document #{source.documentId}
                      </span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500">
                        Page {source.page}
                      </span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500 capitalize">
                        {source.section}
                      </span>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${getSimilarityColor(source.similarity)}`}>
                        {(source.similarity * 100).toFixed(1)}% match
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {source.content}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleViewDocument(source)}
                      className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors"
                      title="View full document"
                    >
                      <Eye className="w-4 h-4" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors"
                      title="Open source"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default SourceDisplay;
